import {
  Component,
  createEffect,
  createMemo,
  createSignal,
  onMount,
  onCleanup,
  Show,
} from 'solid-js'
import { AllChat, characterStore, chatStore, toastStore, userStore } from '/web/store'
import { warmupManager } from '/web/shared/aifans/WarmupManager'
import { AppSchema } from '/common/types'
import { A, useNavigate, useParams } from '@solidjs/router'
import { getAssetUrl } from '/web/shared/util'
import { isLoggedIn } from '/web/store/api'

import ModelPhotos from '/web/shared/aifans/model/ModelPhotos'
import SubscribeToUnlock from '/web/shared/aifans/model/SubscribeToUnlock'
import SuggestedCreators from '/web/shared/aifans/model/SuggestedCreators'
import '/node_modules/flag-icons/css/flag-icons.min.css'
import Footer from '/web/shared/aifans/Footer'
import { redirectToCheckout } from '/web/shared/aifans/checkout'

import { ChatCharacter } from '../Character/util'
import { getModelGallery } from '/web/store/data/gallery'
import { ModelGalleryImage } from '/common/types/aifans-schema'
import { NoTitleModal } from '/web/shared/Modal'
import { aifansSubscriptionApi } from '/web/store/data/aifans-subscription'
import { useInfiniteScroll } from '/web/shared/hooks/useInfiniteScroll'
import { events, EVENTS } from '/web/emitter'
import { abbreviateNumber, getBaseLikes, getModelUrl } from '/web/shared/aifans/lib/model-util'
import ModelPageSignupModal from './ModelPageSignupModal'
import { getOrCreateChatWithCharacter } from '/web/shared/aifans/lib/chat-util'
import { Link, Meta, Title } from '@solidjs/meta'
import { getCanonicalUrl, getWebBaseUrl } from '/web/shared/aifans/lib/config-util'
import { hasActivePremium, hasRoleContentAccess } from '/common/subscription-access'

const ModelPage: Component = () => {
  const params = useParams()
  const nav = useNavigate()

  const [character, setCharacter] = createSignal<AppSchema.Character | undefined>(undefined)
  const [notFound, setNotFound] = createSignal(false)

  const subscribedToModel = userStore().aifansSubscriptions?.find(
    (subscription) =>
      subscription.characterId === character()?._id && subscription.status === 'active'
  )

  const [avatarUrl, setAvatarUrl] = createSignal<string | undefined>(undefined)
  const [vipFeaturedImageUrl, setVipFeaturedImageUrl] = createSignal<string | undefined>(undefined)
  const [country, setCountry] = createSignal<string | undefined>(undefined)
  const [liveChatId, setLiveChatId] = createSignal<string | undefined>(undefined)
  const [modelGalleryImages, setModelGalleryImages] = createSignal<ModelGalleryImage[]>([])
  const [modalImage, setModalImage] = createSignal<string | undefined>(undefined)
  const [isSubscribed, setIsSubscribed] = createSignal<boolean>(false)
  const [subscribing, setSubscribing] = createSignal<boolean>(!!subscribedToModel)
  const [showSubscribeModal, setShowSubscribeModal] = createSignal<boolean>(false)
  const [liking, setLiking] = createSignal(false)
  const [isLiked, setIsLiked] = createSignal(false)
  const [likeCount, setLikeCount] = createSignal('')

  const [hasMore, setHasMore] = createSignal(true)
  const [page, setPage] = createSignal(1)
  const pageSize = 15 // Adjust this value as needed

  const chars = characterStore((s) => {
    return {
      list: s.characters.list,
      map: s.characters.list.reduce<Record<string, AppSchema.Character>>(
        (prev, curr) => Object.assign(prev, { [curr._id]: curr }),
        {}
      ),
      loaded: s.characters.loaded,
    }
  })

  const state = chatStore((s) => ({
    chats: s.allChats.map((chat) => ({
      _id: chat._id,
      name: chat.name,
      createdAt: chat.createdAt,
      updatedAt: chat.updatedAt,
      characterId: chat.characterId,
      characters: toChatListState(s.allChars.map, chat),
      messageCount: chat.messageCount,
    })),
    chars: s.allChars.map,
    allChats: s.allChats,
  }))

  events.on(EVENTS.charUpdated, (char: AppSchema.Character, action: 'created' | 'updated') => {
    const baseLikes = getBaseLikes(char?._id)

    const likes = char?.likes ? char.likes + baseLikes : baseLikes
    setLikeCount(abbreviateNumber(likes))
  })

  onMount(() => {
    if (!chars.loaded) {
      characterStore.getCharacters(true)
    }

    onCleanup(() => {
      warmupManager.cleanup()
    })
  })

  createEffect(() => {
    // Wait for characters to load before checking
    if (!chars.loaded) {
      return
    }

    // Find character by ID or slug, ensuring it matches the category slug if provided
    const char = chars.list.find(
      (c) =>
        (c._id === params.id || c.slug === params.id) &&
        (!params.categorySlug || c.categorySlug === params.categorySlug)
    )

    if (char) {
      setCharacter(char)
      setNotFound(false)

      // Fire off throttled warm up request when character changes
      warmupManager.triggerWarmUp()
    } else {
      setNotFound(true)
      return
    }

    if (!!character()) {
      if (char?.country) {
        let regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
        setCountry(regionNames.of(char?.country))
      }
    }

    const avatar = character()?.avatar
    if (avatar) {
      setAvatarUrl(getAssetUrl(avatar, true))
    }

    const vipFeaturedImage = character()?.vipFeaturedImage
    if (vipFeaturedImage) {
      setVipFeaturedImageUrl(getAssetUrl(vipFeaturedImage, true))
    }

    if (char?._id) {
      const baseLikes = getBaseLikes(char?._id)

      const likes = char?.likes ? char.likes + baseLikes : baseLikes
      setLikeCount(abbreviateNumber(likes))

      loadModelGallery(1)
    }
  })

  const chats = createMemo(() => {
    const filterCharId = character()?._id

    return state.chats.filter((chat) => {
      if (filterCharId && !chat.characters.some((c) => c._id === filterCharId)) return false

      return true
    })
  })

  createEffect(() => {
    const latestChat = chats().sort(function (a, b) {
      // Turn your strings into dates, and then subtract them
      // to get a value that is either negative, positive, or zero.
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
    })

    if (latestChat && latestChat[0]) {
      setLiveChatId(latestChat[0]._id)
    }

    userStore.loadAIFansSubscriptions()
  })

  createEffect(() => {
    const subs = userStore().aifansSubscriptions
    const subscribedToModel = subs?.find(
      (subscription) =>
        subscription.characterId === character()?._id && subscription.status === 'active'
    )

    const hasRoleAccess = hasRoleContentAccess(userStore().user?.role)

    setIsSubscribed(!!subscribedToModel || hasRoleAccess || hasActivePremium(subs))

    const charId = character()?._id
    const isLiked = charId ? userStore.getState().profile?.likedCharacters?.includes(charId) : false
    setIsLiked(!!isLiked)
  })

  const onClickToChat = () => {
    if (isLoggedIn()) {
      // if (!isSubscribed()) {
      //   setShowSubscribeModal(true)
      //   return
      // }

      if (character()?._id) {
        getOrCreateChatWithCharacter(character()?._id!, (url) => nav(url))
      }
    } else {
      userStore.loginModal(true)
    }
  }

  const onClickLike = async () => {
    if (liking()) return

    if (!isLoggedIn()) {
      userStore.loginModal(true)
    } else {
      setLiking(true)
      if (!isLiked()) {
        await userStore.likeCharacter(character()?._id!)
      } else {
        await userStore.unlikeCharacter(character()?._id!)
      }
      setLiking(false)
    }
  }

  const onClickToGenerate = () => {
    if (isLoggedIn()) {
      nav(`/generate-image/${character()?._id}`)
    } else {
      userStore.loginModal(true)
    }
  }

  const loadModelGallery = async (pageNumber: number) => {
    const res = await getModelGallery(character()?._id!, pageNumber, pageSize)
    if (res?.result?.images) {
      if (pageNumber === 1) {
        setModelGalleryImages(res.result.images)
      } else {
        setModelGalleryImages((prev) => [...prev, ...res.result!.images])
      }
      setHasMore(res.result.images.length === pageSize)
    }
  }

  const loadMore = () => {
    if (hasMore()) {
      const nextPage = page() + 1
      setPage(nextPage)
      loadModelGallery(nextPage)
    }
  }

  const { setTarget } = useInfiniteScroll(loadMore)

  const showTokensModal = () => {
    setShowSubscribeModal(false)

    if (!isLoggedIn()) {
      userStore.loginModal(true)
      return
    }

    userStore.buyTokensModal(true)
  }

  const subscribeToModel = async () => {
    if (!isLoggedIn()) {
      setShowSubscribeModal(false)
      userStore.loginModal(true)
      return
    }

    try {
      setSubscribing(true)
      const response = await aifansSubscriptionApi.createAIFansSubscription(character()?._id!)

      // #225: redirect to Epoch's hosted payment page instead of embedding it in an iframe.
      if (!redirectToCheckout(response.result?.redirectURL)) {
        setSubscribing(false)
        toastStore.error('There was a problem subscribing to this model. Please try again.')
      }
    } catch (error) {
      setSubscribing(false)
      setShowSubscribeModal(false)
      toastStore.error('There was a problem subscribing to this model. Please try again.')
    }
  }

  const defaultDescription = `Hi I'm Candice Nice, a vibrant influencer who loves to please my followers with a
                cheerful and playful personality. I just love to dress as Harley Quinn and bring her
                to life through cosplay. From intimate moments to exotic adventures, my passion for
                cosplay has no boundaries.`

  const closeSubscribeModal = () => {
    setShowSubscribeModal(false)
    setSubscribing(false)
    userStore.loadAIFansSubscriptions()
    userStore.getProfile()
  }

  return (
    <Show
      when={!notFound()}
      fallback={
        <div class="h-full w-full bg-black">
          <div class="flex h-screen items-center justify-center">
            <div class="text-center">
              <h1 class="mb-4 font-clash text-4xl text-white">404 - Model Not Found</h1>
              <A href="/" class="text-pink-500 hover:text-pink-400">
                Return Home
              </A>
            </div>
          </div>
          <Footer />
        </div>
      }
    >
      {character()?.name && (
        <Title>{`CosplayFans - ${character()?.realName} cosplays as ${character()?.name}`}</Title>
      )}
      <Link
        rel="canonical"
        href={getCanonicalUrl(getModelUrl(character()?.categorySlug, character()?.slug))}
      />
      {character()?.name && (
        <Meta
          name="og:title"
          content={`CosplayFans - ${character()?.realName} cosplays as ${character()?.name}`}
        />
      )}
      {character()?.metaDescription && (
        <>
          <Meta name="og:description" content={character()?.metaDescription} />
          <Meta name="description" content={character()?.metaDescription} />
        </>
      )}
      {avatarUrl() && <Meta name="og:image" content={avatarUrl()} />}
      <div class="min-h-screen" style={{ background: 'var(--cf-bg)' }}>
        {/* Profile Header */}
        <div class="mx-auto max-w-[var(--cf-content-max)] px-[var(--cf-gutter)]">
          <div class="flex flex-col items-center gap-[18px] pb-6 pt-8 text-center sm:flex-row sm:items-start sm:gap-8 sm:pb-8 sm:pt-14 sm:text-left">
            {/* Profile Image: 280px mobile, 280px tablet, 360px desktop */}
            <img
              src={avatarUrl()}
              alt={`${character()?.realName} cosplays as ${character()?.name}`}
              class="h-[280px] w-[280px] max-w-[calc(100vw-32px)] flex-shrink-0 rounded-[20px] object-cover sm:h-[280px] sm:w-[280px] sm:max-w-none lg:h-[360px] lg:w-[360px]"
              style={{
                border: '3px solid rgba(138,43,226,0.4)',
                'box-shadow': '0 16px 40px rgba(138,43,226,0.3)',
              }}
            />

            {/* Profile Info */}
            <div class="min-w-0 flex-1">
              {/* Name + Star */}
              <div
                class="mb-2.5 flex items-center justify-center gap-3 sm:justify-start"
                style={{ 'font-size': 'clamp(28px, 4vw, 44px)' }}
              >
                <h1 class="font-bebas leading-[1.15] tracking-[1px]">{character()?.name}</h1>
                <button
                  onClick={onClickLike}
                  class="text-[22px] transition-all duration-200 hover:scale-[1.15]"
                  style={{ color: isLiked() ? '#ffc107' : 'rgba(255,255,255,0.4)' }}
                >
                  {isLiked() ? '★' : '☆'}
                </button>
              </div>

              {/* Subtitle: realName • flag country */}
              <div class="mb-4 flex items-center justify-center gap-1.5 font-rajdhani text-[14px] font-semibold tracking-[0.5px] text-[var(--cf-muted)] sm:justify-start">
                {character()?.realName} •{' '}
                <span
                  class={`fi fi-${(character()?.country || 'us').toLowerCase()} h-[12px] w-[18px]`}
                />
                {country()}
              </div>

              {/* Action Buttons */}
              <div class="mb-[18px] flex flex-wrap justify-center gap-2 sm:justify-start">
                <button
                  onClick={onClickToChat}
                  class="inline-flex h-10 items-center gap-1.5 rounded-full px-[18px] font-rajdhani text-[12px] font-extrabold uppercase tracking-[1.2px] text-white transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #e63946, #b71c1c)',
                    'box-shadow': '0 4px 14px rgba(230,57,70,0.35)',
                  }}
                >
                  🔴 Live Chat
                </button>
                <Show
                  when={isSubscribed()}
                  fallback={
                    <button
                      onClick={() => setShowSubscribeModal(true)}
                      class="inline-flex h-10 items-center gap-1.5 rounded-full px-[18px] font-rajdhani text-[12px] font-extrabold uppercase tracking-[1.2px] text-white transition-all hover:-translate-y-0.5"
                      style={{
                        background: 'linear-gradient(135deg, #2dc653, #0d8c3a)',
                        'box-shadow': '0 4px 14px rgba(45,198,83,0.3)',
                      }}
                    >
                      💰 Subscribe $7.99
                    </button>
                  }
                >
                  <div
                    class="inline-flex h-10 items-center gap-1.5 rounded-full px-[18px] font-rajdhani text-[12px] font-extrabold uppercase tracking-[1.2px] text-white"
                    style={{
                      background: 'linear-gradient(135deg, #2dc653, #0d8c3a)',
                      'box-shadow': '0 4px 14px rgba(45,198,83,0.3)',
                    }}
                  >
                    ✓ Subscribed · VIP
                  </div>
                </Show>
                <button
                  onClick={onClickToGenerate}
                  class="inline-flex h-10 items-center gap-1.5 rounded-full px-[18px] font-rajdhani text-[12px] font-extrabold uppercase tracking-[1.2px] text-white transition-all hover:-translate-y-0.5"
                  style={{
                    background: 'linear-gradient(135deg, #8A2BE2, #FF3CAC)',
                    'box-shadow': '0 4px 14px rgba(138,43,226,0.4)',
                  }}
                >
                  ★ Generate
                </button>
              </div>

              {/* Bio */}
              <p class="max-w-[640px] text-[15px] leading-[1.7] text-[var(--cf-muted)]">
                {character()?.description || defaultDescription}
              </p>
            </div>
          </div>

          {/* About Section */}
          <div class="border-t border-[var(--cf-hairline)] py-8">
            <h2 class="mb-[18px] font-bebas text-[20px] tracking-[1px] text-white">About Me</h2>
            <div class="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {[
                { icon: '🧠', label: 'Personality', value: character()?.personality },
                { icon: '💼', label: 'Occupation', value: character()?.occupation },
                { icon: '🎽', label: 'Hobbies', value: character()?.hobbies },
                { icon: '💕', label: 'Relationship', value: character()?.relationship },
                { icon: '👱‍♀️', label: 'Hair', value: character()?.hair },
                { icon: '🎂', label: 'Age', value: character()?.age },
                { icon: '👗', label: 'Body', value: character()?.bodyType },
                { icon: '🌸', label: 'Personality 2', value: character()?.breasts },
                { icon: '💎', label: 'Style', value: character()?.butt },
                { icon: '🌏', label: 'Ethnicity', value: character()?.ethnicity },
              ].map((trait) => (
                <Show when={trait.value}>
                  <div class="flex items-center gap-3 rounded-xl border border-[var(--cf-hairline)] bg-[rgba(255,255,255,0.03)] p-4">
                    <span class="flex-shrink-0 text-[24px]">{trait.icon}</span>
                    <div>
                      <div class="font-rajdhani text-[11px] font-semibold uppercase tracking-[1px] text-[var(--cf-muted)]">
                        {trait.label}
                      </div>
                      <div class="font-rajdhani text-[15px] font-bold text-white">
                        {trait.value}
                      </div>
                    </div>
                  </div>
                </Show>
              ))}
            </div>
          </div>

          {/* VIP Banner — shown when subscribed */}
          <Show when={isSubscribed()}>
            <div
              class="relative my-4 flex items-center gap-[18px] overflow-hidden rounded-2xl border border-[rgba(138,43,226,0.45)] px-[22px] py-[18px]"
              style={{
                background:
                  'radial-gradient(ellipse at 0% 0%, rgba(255,60,172,0.22), transparent 60%), radial-gradient(ellipse at 100% 100%, rgba(0,229,255,0.18), transparent 60%), linear-gradient(135deg, #1a1040 0%, #0e0b1f 100%)',
                'box-shadow':
                  '0 0 0 1px rgba(255,255,255,0.04) inset, 0 12px 30px rgba(138,43,226,0.18)',
              }}
            >
              {/* Shine animation */}
              <div
                class="pointer-events-none absolute left-[-60%] top-0 h-full w-[60%]"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)',
                  animation: 'exBannerShine 4.5s ease-in-out infinite',
                }}
              />
              {/* Crown */}
              <div
                class="relative z-[1] grid h-[52px] w-[52px] flex-shrink-0 place-items-center rounded-xl text-[24px]"
                style={{
                  background: 'linear-gradient(135deg, #FFD23F, #FF9800)',
                  'box-shadow': '0 6px 18px rgba(255,152,0,0.4)',
                }}
              >
                👑
              </div>
              {/* Text */}
              <div class="relative z-[1] min-w-0 flex-1">
                <div class="font-rajdhani text-[10px] font-bold uppercase tracking-[1.8px] text-[#FFD23F]">
                  VIP Subscriber · Active
                </div>
                <div class="mt-0.5 font-bebas text-[24px] leading-[1.1] tracking-[1px] text-white">
                  {character()?.name}'s Private Page
                </div>
                <div class="mt-0.5 text-[12.5px] text-[rgba(255,255,255,0.55)]">
                  Full access to every exclusive photo and video. New drops weekly.
                </div>
              </div>
              {/* All Access badge (static, non-interactive) */}
              <span
                class="relative z-[1] flex h-[34px] flex-shrink-0 items-center rounded-full border border-[rgba(0,229,255,0.45)] bg-[rgba(0,229,255,0.1)] px-3.5 font-rajdhani text-[11px] font-bold uppercase tracking-[1px] text-[#00E5FF]"
                style={{ cursor: 'default' }}
              >
                All Access
              </span>
            </div>
          </Show>

          {/* Non-subscribed viewers: standalone Subscribe-To-Unlock card directly under About Me */}
          <Show when={!isSubscribed()}>
            <SubscribeToUnlock
              onSubscribe={() => setShowSubscribeModal(true)}
              subscribing={subscribing()}
              modelName={character()?.name}
              creatorName={character()?.realName}
              backgroundImage={vipFeaturedImageUrl() || avatarUrl()}
            />
          </Show>

          {/* Subscribed viewers: full exclusive gallery */}
          <Show when={isSubscribed()}>
            {/* Gallery Header */}
            <div class="mb-3.5 mt-6 flex flex-wrap items-center justify-between gap-3">
              <h2 class="flex items-center gap-3 font-bebas text-[28px] tracking-[1px]">
                <span
                  class="grid h-6 w-6 place-items-center rounded-md text-[14px]"
                  style={{
                    background: 'var(--cf-grad-brand)',
                    'box-shadow': '0 4px 12px rgba(138,43,226,0.4)',
                  }}
                >
                  🔓
                </span>
                Exclusive Gallery
              </h2>
              <span class="font-rajdhani text-[12px] font-bold uppercase tracking-[1.2px] text-[rgba(255,255,255,0.4)]">
                {modelGalleryImages().length} pieces · updated weekly
              </span>
            </div>

            {/* Gallery Tabs */}
            <div class="mb-4 flex flex-wrap gap-1.5">
              {[
                { id: 'all', label: 'All', count: modelGalleryImages().length },
                { id: 'photos', label: '📸 Photos', count: modelGalleryImages().length },
                { id: 'videos', label: '🎥 Videos', count: 0 },
              ].map((tab, i) => (
                <button
                  class={`inline-flex h-9 items-center gap-1.5 rounded-full border px-4 font-rajdhani text-[12.5px] font-bold transition-all ${
                    i === 0
                      ? 'border-[rgba(255,60,172,0.55)] text-white'
                      : 'border-[rgba(255,255,255,0.07)] text-[rgba(255,255,255,0.55)] hover:border-[rgba(138,43,226,0.3)]'
                  }`}
                  style={
                    i === 0
                      ? {
                          background:
                            'linear-gradient(135deg, rgba(138,43,226,0.25), rgba(255,60,172,0.18))',
                        }
                      : { background: 'rgba(255,255,255,0.04)' }
                  }
                >
                  {tab.label}
                  <span
                    class="grid min-w-[22px] place-items-center rounded-full px-1.5 font-rajdhani text-[11px] font-bold"
                    style={{ height: '18px', background: 'rgba(0,229,255,0.18)', color: '#00E5FF' }}
                  >
                    {tab.count}
                  </span>
                </button>
              ))}
            </div>

            {/* Gallery */}
            <div class="pb-8">
              <ModelPhotos
                locked={!isLoggedIn() || !isSubscribed()}
                images={modelGalleryImages()}
                onImage={(url) => setModalImage(url)}
                onSubscribe={() => setShowSubscribeModal(true)}
                subscribing={subscribing()}
                hasMore={hasMore()}
                setTarget={setTarget}
                modelName={character()?.name}
                creatorName={character()?.realName}
                backgroundImage={vipFeaturedImageUrl() || avatarUrl()}
                characterId={character()?._id!}
              />
            </div>
          </Show>
        </div>
        <SuggestedCreators categorySlug={character()?.categorySlug} currentId={character()?._id} />
        <Footer />
        <NoTitleModal show={!!modalImage()} close={() => setModalImage()} maxWidth="half">
          <div class="flex justify-center p-4">
            <img class="rounded-md" src={getAssetUrl(modalImage() || '', true)} />
          </div>
        </NoTitleModal>
        <ModelPageSignupModal
          show={showSubscribeModal()}
          subscribeToModel={subscribeToModel}
          showTokensModal={showTokensModal}
          setShowSubscribeModal={closeSubscribeModal}
          avatarUrl={vipFeaturedImageUrl() || avatarUrl()}
          creatorName={character()?.realName}
          characterName={character()?.name}
          subscribing={subscribing()}
        />
      </div>
    </Show>
  )
}

function toCharacterIds(characters?: Record<string, boolean>) {
  if (!characters) return []

  const ids: string[] = []
  for (const [id, enabled] of Object.entries(characters)) {
    if (enabled) ids.push(id)
  }
  return ids
}

function toChatListState(chars: Record<string, AppSchema.Character>, chat: AllChat) {
  const charIds = [chat.characterId].concat(toCharacterIds(chat.characters))

  const seen = new Set<string>()
  const rows: ChatCharacter[] = []
  for (const id of charIds) {
    if (seen.has(id)) continue
    seen.add(id)
    const char = chars[id]
    if (!char) {
      rows.push({ _id: '', name: 'Unknown', description: '', avatar: '' })
      continue
    }

    rows.push({
      _id: char._id,
      name: char.name,
      description: char.description || '',
      avatar: char.avatar,
    })
  }

  return rows
}

export default ModelPage
